"use client";

import React from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { cn } from "@/lib/utils";
import { IFooter } from "@/sanity/lib/definitions";
import { resolvePageLink } from "@/sanity/globals/CTA";
import { HubSpotCookieButton } from "../ui/HubSpotCookieButton";

type FooterType = "lp" | "minimal" | "bar" | "hidden";
type BarMobileVariant = "pill" | "flush" | "flat";

// ─────────────────────────────────────────────────────────────────────────────
// SimpleFooter — visual variants driven by `footerType`:
//   • "lp"      — legacy gradient pill (copyright + legal links + cookie btn)
//   • "minimal" — slim glass pill from Figma 43:4898 (copyright left,
//                 legal links separated by white dots on the right)
//   • "bar"     — flush bar with a top hairline, used by the ad landing pages
//                 (Figma 3019:493 / 3537:19776). On mobile it defaults to a
//                 floating blurred pill (Figma 3019:548); `mobileVariant`
//                 "flush" gives the full-bleed translucent bar (Figma
//                 3499:16769) and "flat" the hairline-topped stack with the
//                 copyright above the links (Figma 5143:2813)
//   • "hidden"  — renders nothing
// ─────────────────────────────────────────────────────────────────────────────

export default function SimpleFooter({
  data,
  className = "",
  footerType,
  mobileVariant = "pill",
}: {
  data: IFooter;
  className?: string;
  footerType: FooterType;
  /** "bar" only — how the bar renders on mobile (see BarFooter). */
  mobileVariant?: BarMobileVariant;
}) {
  if (footerType === "hidden" || !data) return null;

  if (footerType === "minimal")
    return (
      <MinimalFooter
        data={data}
        className={className}
      />
    );

  if (footerType === "bar")
    return (
      <BarFooter
        data={data}
        className={className}
        mobileVariant={mobileVariant}
      />
    );

  return (
    <LpFooter
      data={data}
      className={className}
    />
  );
}

// ─── "bar" — Figma 3019:493 / 3537:19776 (desktop) + 3019:548 (mobile) ───────
//   • Desktop → flush full-width bar with a top hairline, content held to the
//               150rem page container: copyright LEFT, legal links RIGHT
//   • Mobile  → "pill": floating blurred pill, links wrap on top, hairline
//               divider, copyright at the bottom (flex-col-reverse gives that
//               order). "flush": the same stack as a full-bleed translucent
//               bar. "flat" (Figma 5143:2813): hairline-topped translucent
//               bar, copyright first, links wrapping underneath, no divider.
function BarFooter({
  data,
  className,
  mobileVariant = "pill",
}: {
  data: IFooter;
  className?: string;
  mobileVariant?: BarMobileVariant;
}) {
  const legalLinks = (data.legalLinks ?? []).filter(Boolean);
  const flat = mobileVariant === "flat";

  if (!data.copyright && legalLinks.length === 0) return null;

  return (
    <footer
      className={cn(
        "relative z-[999]  w-full lg:pb-0 lg:bg-transparent lg:backdrop-blur-none lg:border-t lg:border-white/10",
        mobileVariant === "flush" && "bg-white/[0.06] backdrop-blur-[2.425rem] pb-0",
        mobileVariant === "pill" && "pb-[3rem]",
        flat && "border-t border-white/10 bg-black/[0.04] backdrop-blur-[2] pb-0",
        className,
      )}>
      <div
        className={cn(
          "mx-auto flex items-center justify-center lg:w-[min(150rem,100%)] lg:flex-row lg:justify-between lg:rounded-none lg:backdrop-blur-none lg:gap-[2rem] lg:py-[3rem]",
          flat
            ? "flex-col gap-[2.5rem] py-[3rem] w-[min(150rem,calc(100%-4rem))]"
            : "flex-col-reverse gap-[1.75rem] py-[1.875rem]",
          mobileVariant === "flush" && "w-[min(45.25rem,calc(100%-4rem))]",
          mobileVariant === "pill" && "w-[min(49.8125rem,calc(100%-4rem))] rounded-[1.25rem] backdrop-blur-[2.425rem]",
        )}>
        {/* Copyright — DOM first; appears LAST on mobile (col-reverse), LEFT on desktop. */}
        {data.copyright && (
          <p className="m-0 text-center font-geist text-[2rem] leading-[3rem] text-white/50">{data.copyright}</p>
        )}

        {/* Divider — mobile only, between links (top) and copyright (bottom). */}
        {!flat && (
          <hr
            aria-hidden
            className="m-0 w-[45.25rem] max-w-full border-0 border-t border-white/10 lg:hidden"
          />
        )}

        {/* Legal links — DOM last; appears FIRST on mobile, RIGHT on desktop. */}
        {legalLinks.length > 0 && (
          <div
            className={cn(
              "flex flex-wrap justify-center items-center lg:justify-end lg:gap-[1.75rem] lg:max-w-none",
              flat ? "gap-[1.75rem] max-w-[33.375rem]" : "gap-[1rem] max-w-[39.25rem]",
            )}>
            {legalLinks.map((item, i) => {
              const href = resolvePageLink(item?.link ?? null) ?? "";
              const isExternal = item?.link?.externalLink ?? false;

              return (
                <React.Fragment key={i}>
                  {href ? (
                    <Link
                      href={href}
                      target={isExternal ? "_blank" : "_self"}
                      className="font-geist text-[2rem] leading-[3rem] whitespace-nowrap text-white/50 transition-colors duration-200 hover:text-white">
                      {item.title}
                    </Link>
                  ) : (
                    <span className="font-geist text-[2rem] leading-[3rem] whitespace-nowrap text-white/50">
                      {item.title}
                    </span>
                  )}
                  {i < legalLinks.length - 1 && (
                    <span
                      aria-hidden
                      className="h-[0.75rem] w-[0.75rem] shrink-0 rounded-full bg-white/50"
                    />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        )}
      </div>
    </footer>
  );
}

// ─── "minimal" — Figma 43:4898 (desktop) + 43:8413 (mobile) ─────────────────
//   • Desktop  → wide pill, copyright LEFT and links RIGHT (flex-row)
//   • Mobile   → compact pill, links wrap on top, divider, copyright at bottom
//                (flex-col-reverse stacks them in the right visual order)
function MinimalFooter({ data, className }: { data: IFooter; className?: string }) {
  const legalLinks = (data.legalLinks ?? []).filter(Boolean);

  return (
    <footer className={cn("relative z-[999] w-full pb-[3rem] lg:pb-[4rem]", className)}>
      <div className="mx-auto flex w-[min(175.375rem,calc(100%-4rem))] flex-col-reverse items-center justify-between gap-[1.75rem] rounded-[1.25rem] border border-white/[0.06] bg-black/[0.32] px-[2.25rem] py-[1.875rem] backdrop-blur-[2.425rem] lg:min-h-[6.75rem] lg:flex-row lg:gap-[2rem] lg:rounded-[2rem] lg:bg-white/[0.04] lg:px-[4rem]">
        {/* Copyright — DOM first; appears LAST on mobile (col-reverse), LEFT on desktop. */}
        {data.copyright && (
          <p className="m-0 text-center font-geist text-[1.75rem] leading-[3rem] text-white/[0.79] lg:text-left">
            {data.copyright}
          </p>
        )}

        {/* Divider — mobile only, between links (top) and copyright (bottom). */}
        <hr
          aria-hidden
          className="m-0 w-[34.25rem] max-w-full border-0 border-t border-white/10 lg:hidden"
        />

        {/* Legal links — DOM last; appears FIRST on mobile, RIGHT on desktop. */}
        {legalLinks.length > 0 && (
          <div className="flex max-w-[39.25rem] flex-wrap items-center justify-center gap-[1rem] lg:max-w-none lg:justify-end lg:gap-[1.75rem]">
            {legalLinks.map((item, i) => {
              const href = resolvePageLink(item?.link ?? null) ?? "";
              const isExternal = item?.link?.externalLink ?? false;

              return (
                <React.Fragment key={i}>
                  {href ? (
                    <Link
                      href={href}
                      target={isExternal ? "_blank" : "_self"}
                      className="font-geist text-[1.75rem] leading-[3rem] text-white/[0.79] transition-opacity duration-200 hover:opacity-100">
                      {item.title}
                    </Link>
                  ) : (
                    <span className="font-geist text-[1.75rem] leading-[3rem] text-white/[0.79]">{item.title}</span>
                  )}
                  {i < legalLinks.length - 1 && (
                    <span
                      aria-hidden
                      className="h-[0.75rem] w-[0.75rem] shrink-0 rounded-full bg-white/80"
                    />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        )}
      </div>
    </footer>
  );
}

// ─── "lp" — legacy gradient pill ─────────────────────────────────────────────
function LpFooter({ data, className }: { data: IFooter; className?: string }) {
  const pathname = usePathname();

  return (
    <footer className={cn("relative z-[999] pt-[4.5rem] lg:pt-0 pb-[4.5rem]", className)}>
      <div>
        <div className="mx-auto flex w-[95%] max-w-[123rem] flex-col-reverse items-center gap-[2rem] rounded-[1rem] border border-white/10 bg-white/5 bg-gradient-to-b from-[#0B0F13] from-[11.57%] to-[#98D8FF] to-[719.35%] px-[2rem] py-[1.85rem] backdrop-blur-[1rem] lg:min-h-[6.75rem] lg:w-full lg:flex-row lg:justify-between lg:rounded-[2rem] lg:px-[4rem]">
          <p className="mb-0 text-[1.75rem] leading-none">
            © World Class Edge {new Date().getFullYear()} - All rights reserved
          </p>
          <hr className="w-[34rem] border-t border-white/10 lg:hidden" />
          <div className="flex flex-wrap items-center justify-center gap-[1.25rem] px-[3rem] sm:gap-[2rem] lg:px-0">
            {data?.legalLinks &&
              data.legalLinks.map((item, index) => {
                const href =
                  item?.link.externalLink && item?.link.url
                    ? item?.link.url
                    : (item?.link.internalLink?.settings?.slug?.current ?? "");

                const isActive = pathname === href;

                return href ? (
                  <React.Fragment key={index}>
                    <Link
                      id={"footerLink" + index}
                      className={cn(
                        "text-[1.75rem] sm:text-[1.75rem] text-white opacity-80 hover:opacity-100 transition-opacity leading-none",
                        isActive && "!opacity-100 underline",
                      )}
                      href={href}
                      target={item?.link.openInNewTab ? "_blank" : "_self"}>
                      <span>{item.title}</span>
                    </Link>
                    {index !== data.legalLinks.length - 1 && (
                      <div
                        className={cn(
                          "flex-shrink-0 rounded-full w-[.75rem] h-[.75rem] bg-[#6B9EBD]",
                          index % 2 == 1 && "hidden lg:block",
                        )}
                      />
                    )}
                  </React.Fragment>
                ) : null;
              })}
            <div className="h-[.75rem] w-[.75rem] flex-shrink-0 rounded-full bg-[#6B9EBD] lg:block" />
            <div className="hidden lg:block">
              <HubSpotCookieButton />
            </div>
          </div>

          <div className="lg:hidden">
            <HubSpotCookieButton />
          </div>
        </div>
      </div>
    </footer>
  );
}
